import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../auth/msg91Widget";
import { config } from "../config";
import { maskMobile, normalizeIndianMobile } from "../api/login";
import { passwordProblems } from "../api/register";
import { claimAvailability, claimInvite, inviteFromSearch, type ClaimAvailability, type ClaimOutcome } from "../api/sponsorClaim";
import { HumanCheck } from "../components/HumanCheck";
import { PasswordField } from "../components/PasswordField";

/** Seconds before the widget allows another send (MSG91 answered 704 before ~33 s). */
const RESEND_AFTER = 35;
const BUSY_RETRY = 60;

type Step = "details" | "code" | "done";

interface Form {
  firstName: string;
  lastName: string;
  mobile: string;
  password: string;
  confirm: string;
}
const EMPTY: Form = { firstName: "", lastName: "", mobile: "", password: "", confirm: "" };

type Notice =
  | { kind: "problems"; problems: string[] }
  | { kind: "busy"; retryAfter: number }
  | { kind: "widget_error"; message: string }
  | { kind: "invalid_code" }
  | { kind: "resent" }
  | { kind: "outcome"; outcome: Exclude<ClaimOutcome, { kind: "created" | "linked" | "incomplete" | "busy" }> };

export function claimFormProblems(f: Form): string[] {
  const p: string[] = [];
  if (!f.firstName.trim()) p.push("Enter your first name.");
  if (!f.lastName.trim()) p.push("Enter your last name.");
  if (normalizeIndianMobile(f.mobile) === null) p.push("Enter the 10-digit mobile number the invitation was sent for.");
  p.push(...passwordProblems(f.password));
  if (f.password !== f.confirm) p.push("The two passwords do not match.");
  return p;
}

/**
 * /sponsor/claim?invite=… -- an organisation's staff member accepts the
 * invitation their service provider sent, and gets a `sponsor` login (design
 * §13.4, §13.5).
 *
 * Details, then the MSG91 code, then the claim -- the order the sign-up page
 * uses. radaccess proves the mobile before it looks at the link.
 *
 * 🔑 NO PREVIEW OF THE INVITATION. Nothing is fetched with the link before the
 * mobile is proved: a page that showed the organisation for any link would be
 * a way to test links. The organisation's name is shown only once claimed.
 *
 * ⛔ PUBLIC. No identity is read and no token is sent.
 */
export function SponsorClaim() {
  const invite = inviteFromSearch(useLocation().search);
  const widgetCfg = config().msg91;
  const [form, setForm] = useState<Form>(EMPTY);
  const [step, setStep] = useState<Step>("details");
  const [code, setCode] = useState("");
  const [reqId, setReqId] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [done, setDone] = useState<{ linked: boolean; organisation: string; note: string } | null>(null);
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const widgetRef = useRef<Msg91Widget | null>(null);
  // A THROTTLED claim never looked at the token, so after "busy" the same
  // token is retried instead of asking for another code.
  const tokenRef = useRef<string>("");
  const codeRef = useRef<HTMLInputElement>(null);

  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };

  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  // Start the widget as the page opens: it fetches its own settings (the
  // captcha included) after it starts, and a send before they arrive fails.
  useEffect(() => {
    if (!widgetCfg) return;
    loadMsg91Widget(widgetCfg)
      .then((w) => {
        widgetRef.current = w;
      })
      .catch(() => undefined);
  }, [widgetCfg]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  // Ask once, as the page opens, whether claiming can work at all -- so an
  // SMS code is never sent for a claim that cannot succeed. See
  // claimAvailability for why this reveals nothing about any invitation.
  const [availability, setAvailability] = useState<ClaimAvailability>("unknown");
  useEffect(() => {
    if (!invite) return;
    const ctl = new AbortController();
    void claimAvailability(ctl.signal).then(setAvailability);
    return () => ctl.abort();
  }, [invite]);
  const claimDown = availability === "unavailable";

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const backToDetails = () => {
    tokenRef.current = "";
    setReqId("");
    setCode("");
    setStep("details");
  };

  const widgetNotice = (err: unknown): Notice =>
    err instanceof WidgetError && err.throttled
      ? { kind: "busy", retryAfter: BUSY_RETRY }
      : { kind: "widget_error", message: err instanceof WidgetError ? err.message : "The SMS service could not be reached" };

  const onDetailsSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!widgetCfg || claimDown) return;
      const problems = claimFormProblems(form);
      if (problems.length > 0) {
        setNotice({ kind: "problems", problems });
        return;
      }
      const e164 = normalizeIndianMobile(form.mobile);
      if (e164 === null) return;
      try {
        const widget = await loadMsg91Widget(widgetCfg);
        widgetRef.current = widget;
        const id = await widget.send(e164);
        tokenRef.current = "";
        setReqId(id);
        setMasked(maskMobile(e164));
        setResendIn(RESEND_AFTER);
        setCode("");
        setStep("code");
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });
  };

  const resend = () =>
    run(async () => {
      const widget = widgetRef.current;
      if (!widget || !reqId) {
        backToDetails();
        return;
      }
      try {
        await widget.resend("sms", reqId);
        setResendIn(RESEND_AFTER);
        setNotice({ kind: "resent" });
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });

  const onCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const widget = widgetRef.current;
      if (!widget || !reqId) {
        backToDetails();
        return;
      }
      let token = tokenRef.current;
      if (!token) {
        try {
          token = await widget.verify(code.trim(), reqId);
        } catch (err) {
          if (err instanceof WidgetError && err.throttled) {
            setNotice({ kind: "busy", retryAfter: BUSY_RETRY });
          } else {
            setNotice({ kind: "invalid_code" });
            setCode("");
          }
          return;
        }
        tokenRef.current = token;
      }

      const r = await claimInvite({
        invite,
        mobile: form.mobile,
        firstName: form.firstName,
        lastName: form.lastName,
        password: form.password,
        widgetToken: token,
      });
      switch (r.kind) {
        case "created":
        case "linked":
          tokenRef.current = "";
          setDone({ linked: r.kind === "linked", organisation: r.organisation, note: r.note });
          setForm(EMPTY);
          setStep("done");
          return;
        case "busy":
          setNotice({ kind: "busy", retryAfter: r.retryAfter });
          return;
        case "incomplete":
          setNotice({ kind: "problems", problems: r.problems });
          backToDetails();
          return;
        default:
          // Every other answer spent the token.
          setNotice({ kind: "outcome", outcome: r });
          backToDetails();
          return;
      }
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Accept your invitation</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your service provider invited you to manage your organisation&apos;s sponsored provider list. Confirm the mobile number
          the invitation was sent for, choose a password, and you can sign in.
        </Typography>

        {!invite && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>This link is incomplete</AlertTitle>
            Open the whole link your provider sent you, or ask them for a new one.
          </Alert>
        )}
        {!widgetCfg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Mobile verification is not available right now</AlertTitle>
            Try again later.
          </Alert>
        )}
        {claimDown && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Accepting invitations is not available right now</AlertTitle>
            No code has been sent. Try this link again later, or tell your provider.
          </Alert>
        )}
        {notice && <NoticeAlert notice={notice} />}

        {step === "details" && invite && (
          <Paper variant="outlined" component="form" onSubmit={onDetailsSubmit} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="First name" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} autoComplete="given-name" fullWidth required />
                <TextField label="Last name" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} autoComplete="family-name" fullWidth required />
              </Stack>
              <TextField
                label="Mobile number"
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                autoComplete="tel-national"
                helperText="The number your provider invited. We send it a code."
                required
              />
              <PasswordField
                label="Password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                autoComplete="new-password"
                helperText="If this number already has a login here, keep your existing password; this one is not used."
                required
              />
              <PasswordField label="Confirm password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} autoComplete="new-password" required />
              <Button type="submit" variant="contained" disabled={busy || !widgetCfg || claimDown}>Send code</Button>
            </Stack>
          </Paper>
        )}

        {step === "code" && (
          <Paper variant="outlined" component="form" onSubmit={onCodeSubmit} sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="body2">Enter the code sent to {masked}.</Typography>
              <TextField
                inputRef={codeRef}
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoComplete="one-time-code"
                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 8 } }}
                required
              />
              <HumanCheck action="invite" />
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={busy || !code.trim()}>Accept invitation</Button>
                <Button disabled={busy || resendIn > 0} onClick={() => void resend()}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </Button>
                <Button disabled={busy} onClick={backToDetails}>Back</Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {step === "done" && done && (
          <Alert severity="success" data-testid="claim-done">
            <AlertTitle>{done.organisation ? `You are now ${done.organisation}'s sponsor` : "Invitation accepted"}</AlertTitle>
            {done.note || (done.linked ? "Sign in with your mobile number and your existing password." : "Sign in with your mobile number and password.")}
            <Box sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" component={RouterLink} to="/">Sign in</Button>
            </Box>
          </Alert>
        )}
      </Container>
    </Box>
  );
}

function NoticeAlert({ notice }: { notice: Notice }) {
  switch (notice.kind) {
    case "problems":
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Please check these</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {notice.problems.map((p) => <li key={p}>{p}</li>)}
          </Box>
        </Alert>
      );
    case "busy":
      return <Alert severity="info" sx={{ mb: 2 }}>Too many attempts. Wait about {Math.ceil(notice.retryAfter / 60)} minute(s) and try again.</Alert>;
    case "widget_error":
      return <Alert severity="error" sx={{ mb: 2 }}>{notice.message}</Alert>;
    case "invalid_code":
      return <Alert severity="error" sx={{ mb: 2 }}>That code is not right. Check it and try again.</Alert>;
    case "resent":
      return <Alert severity="info" sx={{ mb: 2 }}>A new code is on its way.</Alert>;
    case "outcome":
      return <OutcomeAlert outcome={notice.outcome} />;
  }
}

function OutcomeAlert({ outcome }: { outcome: Extract<Notice, { kind: "outcome" }>["outcome"] }) {
  switch (outcome.kind) {
    case "bad_link":
      // One message for every reason -- see the page comment.
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>This invitation link is not valid</AlertTitle>
          It may have expired, been used or cancelled, or be for another mobile number. Ask your provider for a new one.
        </Alert>
      );
    case "not_verified":
      return <Alert severity="error" sx={{ mb: 2 }}>This mobile number was not verified. Send a new code and try again.</Alert>;
    case "conflict":
      return <Alert severity="warning" sx={{ mb: 2 }}>{outcome.message}</Alert>;
    case "retry":
      return <Alert severity="warning" sx={{ mb: 2 }}>{outcome.message}</Alert>;
    case "unavailable":
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Accepting invitations is not available right now</AlertTitle>
          Nothing was changed. Try again later, or tell your provider.
        </Alert>
      );
  }
}
